import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useUser } from '../UserContext';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('manageUsers');
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [users, setUsers] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const { user } = useUser();

  useEffect(() => {
    fetchCategories();
    fetchUsersData();
    fetchTestResults();
  }, []);

  const fetchTestResults = async () => {
    try {
      const resultsSnapshot = await getDocs(collection(db, 'testResults'));
      const resultsList = resultsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTestResults(resultsList);
    } catch (error) {
      console.error('Error fetching test results:', error);
      setError('Failed to fetch test results. Please try again.');
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesSnapshot = await getDocs(collection(db, 'toolCategories'));
      const categoriesList = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(categoriesList);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to fetch categories. Please try again.');
    }
  };

  const fetchUsersData = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users. Please try again.');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      await addDoc(collection(db, 'toolCategories'), { name: newCategory.trim() });
      setNewCategory('');
      setSuccessMessage('Category added successfully.');
      fetchCategories();
    } catch (error) {
      console.error('Error adding category:', error);
      setError('Failed to add category. Please try again.');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'toolCategories', categoryId));
        setSuccessMessage('Category deleted successfully.');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        setError('Failed to delete category. Please try again.');
      }
    }
  };

  const renderTestResults = () => (
    <div>
      <h2 className="text-2xl font-bold mb-4">Well Test Results</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 border-b">Email</th>
              <th className="px-4 py-2 border-b">Address</th>
              <th className="px-4 py-2 border-b">Test Date</th>
              <th className="px-4 py-2 border-b">Status</th>
              <th className="px-4 py-2 border-b">Document</th>
              <th className="px-4 py-2 border-b">Notes</th>
            </tr>
          </thead>
          <tbody>
            {testResults.map((result) => (
              <tr key={result.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 border-b">{result.email}</td>
                <td className="px-4 py-2 border-b">{result.address}</td>
                <td className="px-4 py-2 border-b">{result.testDate}</td>
                <td className="px-4 py-2 border-b">
                  <span className={`px-2 py-1 rounded text-sm ${
                    result.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    result.status === 'processed' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {result.status}
                  </span>
                </td>
                <td className="px-4 py-2 border-b">
                  {result.fileUrl && (
                    <a
                      href={result.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 underline"
                    >
                      View Document
                    </a>
                  )}
                </td>
                <td className="px-4 py-2 border-b">
                  {result.notes && (
                    <div className="max-w-xs truncate" title={result.notes}>
                      {result.notes}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderManageUsers = () => (
    <div>
      <h2 className="text-2xl font-bold mb-4">Manage Users</h2>
      <table className="min-w-full bg-white">
        <thead>
          <tr>
            <th className="py-2">Name</th>
            <th className="py-2">Email</th>
            <th className="py-2">Signup Date</th>
            <th className="py-2">Address</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td className="py-2">{user.name}</td>
              <td className="py-2">{user.email}</td>
              <td className="py-2">{new Date(user.createdAt.seconds * 1000).toLocaleDateString()}</td>
              <td className="py-2">{user.address}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCategories = () => (
    <div>
      <h2 className="text-2xl font-bold mb-4">Manage Tool Categories</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && <p className="text-green-500 mb-4">{successMessage}</p>}
      <form onSubmit={handleAddCategory} className="mb-6">
        <input
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="New category name"
          className="p-2 border rounded mr-2"
        />
        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
          Add Category
        </button>
      </form>
      <ul className="space-y-2">
        {categories.map((category) => (
          <li key={category.id} className="flex justify-between items-center p-2 bg-gray-100 rounded">
            <span>{category.name}</span>
            <button
              onClick={() => handleDeleteCategory(category.id)}
              className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderTestingTools = () => (
    <div>
      <h2 className="text-2xl font-bold mb-4">Testing Tools</h2>
      <form>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fakeUserName">
            Fake User Name
          </label>
          <input
            type="text"
            id="fakeUserName"
            placeholder="Enter fake user name"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fakeUserEmail">
            Fake User Email
          </label>
          <input
            type="email"
            id="fakeUserEmail"
            placeholder="Enter fake user email"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fakeToolName">
            Fake Tool Name
          </label>
          <input
            type="text"
            id="fakeToolName"
            placeholder="Enter fake tool name"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Add Fake Data
        </button>
      </form>
    </div>
  );

  if (!user || !user.isAdmin) {
    return <div className="container mx-auto px-4 py-8">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>
      <div className="flex mb-6 flex-wrap">
        <button
          className={`mr-4 mb-2 px-4 py-2 rounded ${activeTab === 'manageUsers' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('manageUsers')}
        >
          Manage Users
        </button>
        <button
          className={`mr-4 mb-2 px-4 py-2 rounded ${activeTab === 'categories' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button
          className={`mr-4 mb-2 px-4 py-2 rounded ${activeTab === 'testingTools' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('testingTools')}
        >
          Testing Tools
        </button>
        <button
          className={`mr-4 mb-2 px-4 py-2 rounded ${activeTab === 'testResults' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('testResults')}
        >
          Well Test Results
        </button>
      </div>
      {activeTab === 'manageUsers' && renderManageUsers()}
      {activeTab === 'categories' && renderCategories()}
      {activeTab === 'testingTools' && renderTestingTools()}
      {activeTab === 'testResults' && renderTestResults()}
    </div>
  );
};

export default AdminPage;
